import React, { useState, useEffect, useContext } from "react";
import useToastContext from "hooks/useToastContext";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { apiPut, apiGet, apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { useTranslation } from "react-i18next";
import OInputField from "components/reusable/OInputField";
import formValidation from "utils/formValidation";
import AuthContext from "context/AuthContext";
import ErrorMessage from "components/ErrorMessage";
import OTextArea from "components/reusable/OTextArea";

const VideoEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
  });
  const notification = useToastContext();
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoFileError, setVideoFileError] = useState("");
  const [thumbnailError, setThumbnailError] = useState("");
  const [uploading, setUploading] = useState(false);
  const { updatePageName } = useContext(AuthContext);

  const videoType = watch('videoType', location?.state?.videoType || 'youtube');

  useEffect(() => {
    if (location?.state) {
      reset({
        title: location.state.title,
        description: location.state.description,
        videoLink: location.state.videoLink,
        videoType: location.state.videoType,
      });
    }
  }, [location, reset]);

  const validateYouTubeUrl = (url) => {
    const regex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return regex.test(url);
  };

  const onSubmit = async (data) => {
    try {
      // Validate based on video type
      if (data.videoType === 'youtube' && !validateYouTubeUrl(data.videoLink)) {
        notification.error('Please enter a valid YouTube URL');
        return;
      }

      if (data.videoType === 'upload') {
        if (!videoFile && !location?.state?.videoFile) {
          setVideoFileError('Please upload a video file');
          return;
        }
        if (!thumbnailFile && !location?.state?.thumbnail) {
          setThumbnailError('Please upload a thumbnail');
          return;
        }
      }

      setUploading(true);
      const formData = new FormData();
      formData.append("title", data?.title?.trim());
      formData.append("description", data?.description);
      formData.append("videoType", data.videoType);

      if (data.videoType === 'youtube') {
        formData.append("videoLink", data.videoLink);
        formData.append("videoFile", ''); // Clear uploaded file
        formData.append("thumbnail", ''); // Clear thumbnail
      } else if (data.videoType === 'upload') {
        if (videoFile) {
          formData.append("videoFile", videoFile);
        }
        if (thumbnailFile) {
          formData.append("thumbnail", thumbnailFile);
        }
        formData.append("videoLink", ''); // Clear YouTube link
      }
      
      const path = apiPath.video + "/" + location?.state?._id;
      const result = await apiPut(path, formData);
      if (result.data.success) {
        navigate("/video-management");
        notification.success(result?.data.message);
      } else {
        notification.error(result?.data.message);
      }
    } catch (error) {
      console.log("error in edit video==>>>>", error);
      notification.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    updatePageName(t("EDIT_VIDEO"));
  }, []);

  const handleVideoFileChange = (event) => {
    const file = event.target.files[0]; 
    if (file) {
      // Check file type
      const allowedTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/mkv', 'video/webm'];
      if (!allowedTypes.includes(file.type)) {
        setVideoFileError('Please select a valid video file (MP4, AVI, MOV, WMV, FLV, MKV, WEBM)');
        return;
      }

      // Check file size (100MB max)
      if (file.size > 100 * 1024 * 1024) {
        setVideoFileError('Video file size should be less than 100MB');
        return;
      }

      setVideoFile(file);
      setVideoFileError('');
    }
  };

  const handleThumbnailChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setThumbnailError('Please select a valid image file (JPEG, JPG, PNG, GIF, WEBP)');
        return;
      }

      // Check file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setThumbnailError('Thumbnail size should be less than 5MB');
        return;
      }

      setThumbnailFile(file);
      setThumbnailError('');
    }
  };

  return (
    <>
      <div>
        <div className="bg-[#F9F9F9]">
          <div className="px-3 py-4">
            <div className="bg-white border border-[#E9EDF9] rounded-lg">
              <div className=" border-b-[#E3E3E3] grid gap-2 px-4 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1">
                <div className="col-span-3 flex flex-wrap  items-center" />
              </div>

              <div className="p-5 shadow-md">
                {/* <div className='relative z-0 mb-6 w-full group'>
                  <label className='inline-flex items-center'>
                    <input
                      type='checkbox'
                      {...register('isFeatured')}
                      className='form-checkbox rounded'
                    />
                    <span className='ml-2 text-sm font-medium text-gray-900'>
                      {t('SET_AS_FEATURED_VIDEO')}
                    </span>
                  </label>
                </div> */}
                <OInputField
                  inputLabel={
                    <>
                      {t("TITLE")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  wrapperClassName="relative z-0 mb-6 w-full group"
                  name="title"
                  type="text"
                  autoFocus
                  register={register("title", formValidation["title"])}
                  errors={errors}
                />
                
                <OTextArea
                  wrapperClassName="relative z-0 mb-6 w-full group"
                  inputLabel={<>{t("DESCRIPTION")}</>}
                  type="text"
                  name="description"
                  register={register("description")}
                />

                {/* Video Type Selection */}
                <div className='relative z-0 mb-6 w-full group'>
                  <label className='block mb-2 text-sm font-medium text-gray-900'>
                    {t('VIDEO_TYPE')} <span className='text-red-500'>*</span>
                  </label>
                  <div className='flex space-x-4'>
                    <label className='inline-flex items-center'>
                      <input
                        type='radio'
                        value='youtube'
                        {...register('videoType')}
                        className='form-radio'
                      />
                      <span className='ml-2'>{t('YOUTUBE_LINK')}</span>
                    </label>
                    <label className='inline-flex items-center'>
                      <input
                        type='radio'
                        value='upload'
                        {...register('videoType')}
                        className='form-radio'
                      />
                      <span className='ml-2'>{t('UPLOAD_VIDEO')}</span>
                    </label>
                  </div>
                </div>

                {/* YouTube Link Field */}
                {videoType === 'youtube' && (
                  <OInputField
                    wrapperClassName="relative z-0 mb-6 w-full group"
                    name="videoLink"
                    inputLabel={
                      <>
                        {t('YOUTUBE_LINK')}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    type="text"
                    placeholder="https://www.youtube.com/watch?v=..."
                    register={register("videoLink", {
                      required: videoType === 'youtube' ? 'YouTube link is required' : false,
                      validate: {
                        validUrl: value => 
                          videoType === 'youtube' ? validateYouTubeUrl(value) || 'Please enter a valid YouTube URL' : true
                      }
                    })}
                    errors={errors}
                  />
                )}

                {/* Video Upload Field */}
                {videoType === 'upload' && (
                  <>
                    <div className='relative z-0 mb-6 w-full group'>
                      <label className='block mb-2 text-sm font-medium text-gray-900'>
                        {t('UPLOAD_VIDEO')} <span className='text-red-500'>*</span>
                      </label>
                      <input
                        type='file'
                        accept='video/*'
                        className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'
                        onChange={handleVideoFileChange}
                      />
                      {videoFileError && <ErrorMessage message={videoFileError} />}
                      {videoFile && (
                        <div className='mt-2 text-sm text-green-600'>
                          Selected file: {videoFile.name}
                        </div>
                      )}
                      {location?.state?.videoFile && !videoFile && (
                        <div className='mt-2 text-sm text-gray-600'>
                          Current file: {location.state.videoFile.split('/').pop()}
                        </div>
                      )}
                      <div className='mt-1 text-xs text-gray-500'>
                        Supported formats: MP4, AVI, MOV, WMV, FLV, MKV, WEBM (Max 100MB)
                      </div>
                    </div>

                    {/* Thumbnail Upload Field */}
                    <div className='relative z-0 mb-6 w-full group'>
                      <label className='block mb-2 text-sm font-medium text-gray-900'>
                        {t('THUMBNAIL')} <span className='text-red-500'>*</span>
                      </label>
                      <input
                        type='file'
                        accept='image/*'
                        className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'
                        onChange={handleThumbnailChange}
                      />
                      {thumbnailError && <ErrorMessage message={thumbnailError} />}
                      {thumbnailFile && (
                        <div className='mt-2 text-sm text-green-600'>
                          Selected thumbnail: {thumbnailFile.name}
                        </div>
                      )}
                      {location?.state?.thumbnail && !thumbnailFile && (
                        <div className='mt-2 text-sm text-gray-600'>
                          Current thumbnail: {location.state.thumbnail.split('/').pop()}
                        </div>
                      )}
                      <div className='mt-1 text-xs text-gray-500'>
                        Supported formats: JPEG, JPG, PNG, GIF, WEBP (Max 5MB)
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
        <button
          className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150"
          type="button"
          onClick={() => navigate("/video-management")}
          disabled={uploading}
        >
          {t("O_BACK")}
        </button>

        <button
          className="bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150 disabled:opacity-50"
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={uploading}
        >
          {uploading ? t('UPLOADING') : t("O_UPDATE")}
        </button>
      </div>
    </>
  );
};

export default VideoEdit;